////// Lib. //////
//////////////////

import Component from "gia/Component";
import { gsap } from "gsap";
import { loadLazyElements } from "../../../../app/modules/LazyLoad.js";

///// Assets /////
//////////////////

// Attempts per image to find a spot within the overlap budget before settling
// for the least-overlapping candidate seen.
const PLACEMENT_TRIES = 200;
const PROCESS_MAX_OVERLAP = 0.33;
// These persist across Swup navigations and must never be visually covered by an image.
const EXCLUSION_SELECTORS = ['[data-nav-logo]', '[data-nav-menu-links]'];
const EDGE_CANDIDATES = [
  { xRatio: 0, yRatio: 0 },
  { xRatio: 1, yRatio: 0 },
  { xRatio: 0, yRatio: 1 },
  { xRatio: 1, yRatio: 1 },
];

// Images are kept to the lower half of the screen, as a share of its height.
const REGION_MIN_Y = 0.0;

// Share of an image's height that may hang past the bottom edge, giving the
// band back some of the room the top cutoff takes away.
const REGION_MAX_BLEED = 0.0;

// Minimum clearance kept between an image and the top/right screen edges, in grid units.
const EDGE_MARGIN_UNITS = 2;
const MOBILE_MEDIA_QUERY = '(max-width: 767px), (max-height: 767px) and (pointer: coarse)';

// Comp. class ///
//////////////////

export default class ProcessSteps extends Component {
  /////////// Constructor ////////////
  ////////////////////////////////////

  constructor(el, options) {
    super(el);

    this.ref = {
      item: [],
      imgGroup: [],
    };

    this.options = {
      name: "ProcessSteps",
      duration: 0.4,
      imgmode: "background", // 'background' = full-screen layer, 'panel' = inside the step's panel
      maxoverlap: 0.5, // share of the smaller image's area two images may share
      imgduration: 0.6,
      ...options,
    };

    this._onItemClick = null;
    this._onItemKeyDown = null;
    this._onResize = null;
    this._resizeTimer = null;
    this._groups = [];
    this._activeItem = null;
    this._preloadHandle = null;
    this._onWindowLoad = null;
    this._exclusionEls = EXCLUSION_SELECTORS.map((sel) => document.querySelector(sel)).filter(Boolean);
  }

  ///////////// Mount ////////////////
  ////////////////////////////////////

  mount() {
    if (!this.ref.item.length) return;

    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      this.element.querySelector('.process-steps__bg')?.remove();
      this.ref.imgGroup = [];
    }

    this._onItemClick = (e) => this._toggleItem(e.currentTarget);

    this._onItemKeyDown = (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      this._toggleItem(e.currentTarget);
    };

    this.ref.item.forEach((item) => {
      item.addEventListener("click", this._onItemClick);
      item.addEventListener("keydown", this._onItemKeyDown);
    });

    this._groups = this.ref.imgGroup.map((el) => ({
      el,
      images: Array.from(el.querySelectorAll("img")),
      placements: [],
      bounds: null,
      placed: false,
      loaded: false,
    }));

    this._schedulePreload();

    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._groups.forEach((group) => this._applyGroup(group)), 150);
    };

    window.addEventListener("resize", this._onResize);

    this._openItemImmediate(this.ref.item[0]);

    // Aspect ratios come from the markup, so boxes are measurable without
    // waiting on the images themselves.
    requestAnimationFrame(() => this._setGroupVisible(0, true));
  }

  //////////// Unmount ///////////////
  ////////////////////////////////////

  unmount() {
    if (this._onWindowLoad) window.removeEventListener('load', this._onWindowLoad);
    if (this._preloadHandle !== null) {
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(this._preloadHandle);
      else window.clearTimeout(this._preloadHandle);
    }

    clearTimeout(this._resizeTimer);
    window.removeEventListener("resize", this._onResize);
    this._onResize = null;

    this.ref.item.forEach((item) => {
      item.removeEventListener("click", this._onItemClick);
      item.removeEventListener("keydown", this._onItemKeyDown);
    });

    this._groups.forEach((group) => gsap.killTweensOf(group.images));
    this._groups = [];

    if (this._activeItem) gsap.killTweensOf(this._panelFor(this._activeItem));
  }

  /////////// Private ////////////////
  ////////////////////////////////////

  _panelFor(item) {
    return item?.nextElementSibling?.classList.contains("process-steps__panel") ? item.nextElementSibling : null;
  }

  _openItemImmediate(item) {
    const panel = this._panelFor(item);
    if (!panel) return;

    this._activeItem = item;
    item.dataset.active = "true";
    item.setAttribute("aria-expanded", "true");

    panel.classList.remove("hidden");
    panel.setAttribute("aria-hidden", "false");
    gsap.set(panel, { height: "auto" });
  }

  _toggleItem(item) {
    if (this._activeItem === item) {
      const firstItem = this.ref.item[0];
      this._closeItem(item);
      if (item !== firstItem) this._openItem(firstItem);
      return;
    }

    if (this._activeItem) this._closeItem(this._activeItem);
    this._openItem(item);
  }

  _openItem(item) {
    const panel = this._panelFor(item);
    if (!panel) return;

    this._activeItem = item;
    item.dataset.active = "true";
    item.setAttribute("aria-expanded", "true");

    gsap.killTweensOf(panel);
    panel.classList.remove("hidden");
    panel.setAttribute("aria-hidden", "false");
    gsap.set(panel, { height: 0 });

    const targetHeight = panel.scrollHeight;
    gsap.to(panel, {
      height: targetHeight,
      duration: this.options.duration,
      ease: "power2.inOut",
      onComplete: () => gsap.set(panel, { height: "auto" }),
    });

    // A collapsed panel measures zero, so panel-mode groups are placed against
    // the height the panel is about to animate to.
    this._setGroupVisible(this.ref.item.indexOf(item), true, { width: panel.clientWidth, height: targetHeight });
  }

  _closeItem(item) {
    const panel = this._panelFor(item);
    if (!panel) return;

    if (this._activeItem === item) this._activeItem = null;
    item.dataset.active = "false";
    item.setAttribute("aria-expanded", "false");

    gsap.killTweensOf(panel);
    gsap.set(panel, { height: panel.offsetHeight });
    gsap.to(panel, {
      height: 0,
      duration: this.options.duration,
      ease: "power2.inOut",
      onComplete: () => {
        panel.classList.add("hidden");
        panel.setAttribute("aria-hidden", "true");
        gsap.set(panel, { clearProps: "height" });
      },
    });

    this._setGroupVisible(this.ref.item.indexOf(item), false);
  }

  // ── Background images ──────────────────────────────────────────────────────

  _schedulePreload() {
    const queue = () => {
      const load = () => {
        this._preloadHandle = null;
        this._groups.forEach((group) => {
          if (group.loaded) return;
          group.loaded = true;
          loadLazyElements(group.images);
        });
      };

      this._preloadHandle = 'requestIdleCallback' in window
        ? window.requestIdleCallback(load, { timeout: 2000 })
        : window.setTimeout(load, 0);
    };

    if (document.readyState === 'complete') {
      queue();
      return;
    }

    this._onWindowLoad = () => {
      this._onWindowLoad = null;
      queue();
    };
    window.addEventListener('load', this._onWindowLoad, { once: true });
  }

  _setGroupVisible(index, isVisible, bounds) {
    const group = this._groups[index];
    if (!group || !group.images.length) return;

    if (isVisible) {
      if (!group.loaded) {
        group.loaded = true;
        loadLazyElements(group.images);
      }

      if (!group.placed) this._placeGroup(group, bounds);
    }

    gsap.to(group.images, {
      opacity: isVisible ? 1 : 0,
      duration: this.options.imgduration,
      ease: "power2.out",
      overwrite: "auto",
    });
  }

  // Rejection sampling: keep drawing positions until one stays within the
  // overlap budget against everything already placed, otherwise take the best
  // candidate seen. Positions are stored as ratios of the available range so a
  // resize re-flows them instead of re-rolling new ones.
  _placeGroup(group, bounds) {
    group.bounds = this._boundsOf(group, bounds);
    if (!group.bounds) return;

    const placed = [];

    group.placements = group.images.map((image) => {
      const box = { width: image.offsetWidth, height: image.offsetHeight };
      let best = null;
      let bestScore = Infinity;

      for (let attempt = 0; attempt < PLACEMENT_TRIES; attempt++) {
        const edgeIndex = attempt - (PLACEMENT_TRIES - EDGE_CANDIDATES.length);
        const candidate = edgeIndex >= 0
          ? EDGE_CANDIDATES[edgeIndex]
          : { xRatio: Math.random(), yRatio: Math.random() };
        const rect = this._rectFor(group, box, candidate);

        const imageOverlap = this._maxOverlap(rect, placed);
        const processOverlap = this._processOverlap(rect);
        const processExcess = Math.max(0, processOverlap - PROCESS_MAX_OVERLAP);
        const exclusionOverlap = this._exclusionOverlap(rect);
        const score = processExcess * 1000 + exclusionOverlap * 1000 + imageOverlap;

        if (score < bestScore) {
          bestScore = score;
          best = { candidate, rect };
        }

        if (processOverlap <= PROCESS_MAX_OVERLAP && imageOverlap <= this.options.maxoverlap && exclusionOverlap === 0) break;
      }

      if (!best) return { xRatio: 0, yRatio: 0 };

      placed.push(best.rect);
      return best.candidate;
    });

    group.placed = true;
    this._applyGroup(group);
  }

  _applyGroup(group) {
    const bounds = this._boundsOf(group);

    // A collapsed panel-mode group can't be re-flowed, so it's re-placed the
    // next time its step opens instead of keeping positions from the old size.
    if (!bounds) {
      group.placed = false;
      return;
    }

    group.bounds = bounds;

    group.images.forEach((image, i) => {
      const placement = group.placements[i];
      if (!placement) return;

      const box = { width: image.offsetWidth, height: image.offsetHeight };
      const rect = this._rectFor(group, box, placement);
      gsap.set(image, { x: rect.x, y: rect.y });
    });
  }

  _boundsOf(group, fallback) {
    const width = group.el.clientWidth;
    const height = group.el.clientHeight;

    return width && height ? { width, height } : (fallback ?? null);
  }

  _rectFor(group, box, { xRatio, yRatio }) {
    const bounds = group.bounds;
    const margin = this._edgeMargin();
    const freeWidth = Math.max(0, bounds.width - box.width - margin);
    const { minY, maxY } = this._verticalRange(box, bounds, margin);

    return { x: xRatio * freeWidth, y: minY + yRatio * (maxY - minY), width: box.width, height: box.height };
  }

  // Panel mode fills its box; background mode keeps clear of the upper region
  // and is allowed to bleed past the bottom edge to win that room back.
  _verticalRange(box, bounds, margin = 0) {
    if (this.options.imgmode === "panel") {
      return { minY: 0, maxY: Math.max(0, bounds.height - box.height) };
    }

    const minY = Math.max(bounds.height * REGION_MIN_Y, margin);
    return { minY, maxY: Math.max(minY, bounds.height - box.height * (1 - REGION_MAX_BLEED)) };
  }

  // Only background mode spans the full screen — panel-mode images stay unrestricted, sized to their own panel.
  _edgeMargin() {
    if (this.options.imgmode !== 'background') return 0;

    const unit = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--unit-spacing')) || 30;
    return unit * EDGE_MARGIN_UNITS;
  }

  // Overlap is measured against the smaller of the two boxes, so the budget
  // means the same thing regardless of how differently sized they are.
  _maxOverlap(rect, others) {
    return others.reduce((max, other) => {
      const overlapWidth = Math.min(rect.x + rect.width, other.x + other.width) - Math.max(rect.x, other.x);
      const overlapHeight = Math.min(rect.y + rect.height, other.y + other.height) - Math.max(rect.y, other.y);
      if (overlapWidth <= 0 || overlapHeight <= 0) return max;

      const smallestArea = Math.min(rect.width * rect.height, other.width * other.height);
      return smallestArea ? Math.max(max, (overlapWidth * overlapHeight) / smallestArea) : max;
    }, 0);
  }

  _processOverlap(rect) {
    if (this.options.imgmode !== 'background') return 0;

    const processRect = this.element?.getBoundingClientRect?.();
    if (!processRect?.width || !processRect.height) return 0;

    const overlapWidth = Math.min(rect.x + rect.width, processRect.right) - Math.max(rect.x, processRect.left);
    const overlapHeight = Math.min(rect.y + rect.height, processRect.bottom) - Math.max(rect.y, processRect.top);
    if (overlapWidth <= 0 || overlapHeight <= 0) return 0;

    return (overlapWidth * overlapHeight) / (processRect.width * processRect.height);
  }

  // Logo and bottom menu sit outside this component and must stay fully clear of images.
  _exclusionOverlap(rect) {
    if (this.options.imgmode !== 'background' || !this._exclusionEls.length) return 0;

    return this._exclusionEls.reduce((max, el) => {
      const exRect = el.getBoundingClientRect();
      if (!exRect.width || !exRect.height) return max;

      const overlapWidth = Math.min(rect.x + rect.width, exRect.right) - Math.max(rect.x, exRect.left);
      const overlapHeight = Math.min(rect.y + rect.height, exRect.bottom) - Math.max(rect.y, exRect.top);
      if (overlapWidth <= 0 || overlapHeight <= 0) return max;

      return Math.max(max, (overlapWidth * overlapHeight) / (rect.width * rect.height));
    }, 0);
  }

}
